// TaskFlow · comunicación con el servidor, cola sin conexión y estado de sincronización.
//
// Las lecturas pasan por el service worker: con red traen datos frescos; sin red, la última copia.
// Las escrituras «encolables» (crear tarea, completar, editar…) se guardan en IndexedDB si no hay red
// y se reenvían al reconectar, en orden, con la cabecera X-Operacion para que el servidor no las repita.

import { uuid } from './util.js';

export const estado = {
  csrf: '',
  usuario: null,
  vapid: '',
  fichaEventos: '',
  correoDisponible: false,
  enLinea: navigator.onLine,
  pendientes: 0,
  sincronizando: false,
  errorSync: null,
};
const oyentes = new Set();
export function alCambiarConexion(f) { oyentes.add(f); return () => oyentes.delete(f); }
function avisarCambio() { oyentes.forEach((f) => { try { f(estado); } catch (err) { console.error(err); } }); }

export class ErrorApi extends Error {
  constructor(mensaje, estadoHttp, sinConexion = false) { super(mensaje); this.estado = estadoHttp; this.sinConexion = sinConexion; }
}

async function pedir(metodo, ruta, cuerpo, extra = {}) {
  const op = { method: metodo, headers: { 'X-CSRF': estado.csrf, Accept: 'application/json' }, credentials: 'same-origin' };
  if (extra.operacion) op.headers['X-Operacion'] = extra.operacion;
  if (cuerpo instanceof FormData) op.body = cuerpo;
  else if (cuerpo !== undefined) { op.headers['Content-Type'] = 'application/json'; op.body = JSON.stringify(cuerpo); }
  let r;
  try { r = await fetch(ruta, op); } catch {
    marcarConexion(false);
    throw new ErrorApi('Sin conexión.', 0, true);
  }
  const sinConexion = r.headers.get('X-Sin-Conexion') === '1';
  if (!sinConexion) marcarConexion(true); else marcarConexion(false);
  let datos = {};
  try { datos = await r.json(); } catch { datos = {}; }
  if (r.status === 401 && !ruta.startsWith('/api/acceso')) { window.dispatchEvent(new CustomEvent('tf:sesion-terminada')); }
  if (!r.ok) throw new ErrorApi(datos.error || 'Algo falló. Inténtalo de nuevo.', r.status, sinConexion || datos.sin_conexion);
  if (sinConexion) datos.__sinConexion = true;
  return datos;
}

export const api = {
  get: (ruta) => pedir('GET', ruta),
  post: (ruta, cuerpo = {}) => pedir('POST', ruta, cuerpo),
  subir: (ruta, formData, alProgreso) => new Promise((res, rej) => {
    const x = new XMLHttpRequest();
    x.open('POST', ruta);
    x.setRequestHeader('X-CSRF', estado.csrf);
    x.upload.onprogress = (ev) => { if (ev.lengthComputable && alProgreso) alProgreso(ev.loaded / ev.total); };
    x.onload = () => { let d = {}; try { d = JSON.parse(x.responseText); } catch { /* */ } if (x.status < 400) res(d); else rej(new ErrorApi(d.error || 'No se pudo subir el archivo.', x.status)); };
    x.onerror = () => rej(new ErrorApi('Sin conexión: los adjuntos necesitan internet.', 0, true));
    x.send(formData);
  }),
  /**
   * Escritura que funciona sin conexión: si no hay red, se encola y devuelve {encolada: true}.
   * La interfaz debe aplicar el cambio de forma optimista.
   */
  async enviar(ruta, cuerpo = {}, descripcion = '') {
    const operacion = uuid();
    if (!estado.enLinea) { await encolar({ operacion, ruta, cuerpo, descripcion }); return { encolada: true }; }
    try {
      return await pedir('POST', ruta, cuerpo, { operacion });
    } catch (err) {
      if (err.sinConexion) { await encolar({ operacion, ruta, cuerpo, descripcion }); return { encolada: true }; }
      throw err;
    }
  },
};

// ---------- Cola en IndexedDB ----------
let bdCola = null;
function abrirCola() {
  if (bdCola) return bdCola;
  bdCola = new Promise((res, rej) => {
    const r = indexedDB.open('taskflow-cola', 1);
    r.onupgradeneeded = () => r.result.createObjectStore('operaciones', { keyPath: 'n', autoIncrement: true });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
  return bdCola;
}
async function tx(modo, f) {
  const bd = await abrirCola();
  return new Promise((res, rej) => {
    const t = bd.transaction('operaciones', modo);
    const r = f(t.objectStore('operaciones'));
    t.oncomplete = () => res(r?.result);
    t.onerror = () => rej(t.error);
  });
}
async function encolar(op) {
  await tx('readwrite', (s) => s.add({ ...op, creada: Date.now() }));
  await contarPendientes();
}
export async function contarPendientes() {
  try { estado.pendientes = await tx('readonly', (s) => s.count()); } catch { estado.pendientes = 0; }
  avisarCambio();
  return estado.pendientes;
}
export async function operacionesPendientes() {
  try { return await tx('readonly', (s) => s.getAll()); } catch { return []; }
}

/** Envía la cola en orden. Un error del servidor (no de red) se registra y la operación se descarta para no bloquear la cola. */
export async function sincronizar() {
  if (estado.sincronizando || !estado.enLinea) return;
  const ops = await operacionesPendientes();
  if (!ops.length) { estado.errorSync = null; avisarCambio(); return; }
  estado.sincronizando = true; estado.errorSync = null; avisarCambio();
  // La ficha CSRF puede faltar si la app se abrió sin conexión: se renueva antes de enviar.
  try { const yo = await pedir('GET', '/api/acceso/yo'); if (yo.csrf) estado.csrf = yo.csrf; if (yo.ficha_eventos) estado.fichaEventos = yo.ficha_eventos; } catch { estado.sincronizando = false; avisarCambio(); return; }
  const fallidas = [];
  for (const op of ops) {
    try {
      await pedir('POST', op.ruta, op.cuerpo, { operacion: op.operacion });
      await tx('readwrite', (s) => s.delete(op.n));
    } catch (err) {
      if (err.sinConexion) break; // se reintenta al reconectar
      fallidas.push(`${op.descripcion || op.ruta}: ${err.message}`);
      await tx('readwrite', (s) => s.delete(op.n));
    }
  }
  estado.sincronizando = false;
  if (fallidas.length) estado.errorSync = fallidas;
  await contarPendientes();
  window.dispatchEvent(new CustomEvent('tf:sincronizado', { detail: { fallidas } }));
}

// ---------- Estado de conexión ----------
let comprobando = null;
export function marcarConexion(en) {
  if (estado.enLinea === en) return;
  estado.enLinea = en;
  if (en && comprobando) { clearInterval(comprobando); comprobando = null; }
  avisarCambio();
  if (en) sincronizar();
  else if (!comprobando) comprobando = setInterval(async () => {
    try { const r = await fetch('/api/acceso/yo', { cache: 'no-store', credentials: 'same-origin' }); if (r.ok && r.headers.get('X-Sin-Conexion') !== '1') { clearInterval(comprobando); comprobando = null; marcarConexion(true); } } catch { /* sigue sin red */ }
  }, 10000);
}
window.addEventListener('online', () => marcarConexion(true));
window.addEventListener('offline', () => marcarConexion(false));
